import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './AssetGenerationTool.css';
import './CloudStaffDashboard.css';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaBuilding, FaShieldAlt, FaLayerGroup, FaSearch, FaCogs, FaCheck,
  FaChevronRight, FaDownload, FaEye, FaPlus, FaMinus, FaTag,
  FaExclamationTriangle, FaDatabase, FaFileImport, FaTimes, FaInfoCircle,
  FaBars,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { getApiEndpoint } from '../../../config/api';

// ── Hydrajaws asset configuration ─────────────────────────────────────
// These map to the real CloudSafe database tables and the Hydrajaws tag system

const SAFETY_SECTIONS = {
  roof_safety: {
    label: 'Roof Safety',
    assetTypes: {
      davit_bases:   { label: 'Abseil Systems (Davit Sockets)', code: 'DS', subtypes: [
        { value: 'cast-in socket',       label: 'Cast-in Socket',       fixingDefault: 'cast-in' },
        { value: 'wall mounted socket',  label: 'Wall Mounted Socket',  fixingDefault: 'through-bolt' },
        { value: 'surface mounted socket', label: 'Surface Mounted Socket', fixingDefault: 'through-bolt' },
      ]},
      anchor_points: { label: 'Anchor Points', code: 'AP', subtypes: [
        { value: 'fall_arrest', label: 'Fall Arrest', fixingDefault: 'through-bolt' },
        { value: 'abseil',     label: 'Abseil',      fixingDefault: 'through-bolt' },
      ]},
      static_lines:  { label: 'Static Lines', code: 'SL', subtypes: [
        { value: 'horizontal', label: 'Horizontal', fixingDefault: 'through-bolt' },
        { value: 'vertical',   label: 'Vertical',   fixingDefault: 'through-bolt' },
      ]},
      ladders:       { label: 'Ladders', code: 'LD', subtypes: [
        { value: 'fixed',    label: 'Fixed',    fixingDefault: 'through-bolt' },
        { value: 'portable', label: 'Portable', fixingDefault: null },
        { value: 'cat',      label: 'Cat Ladder', fixingDefault: 'through-bolt' },
        { value: 'caged',    label: 'Caged',    fixingDefault: 'through-bolt' },
      ]},
      handrails:     { label: 'Handrails', code: 'HR', subtypes: [
        { value: 'edge_protection', label: 'Edge Protection', fixingDefault: 'through-bolt' },
        { value: 'parapet',         label: 'Parapet',         fixingDefault: 'through-bolt' },
        { value: 'guardrail',       label: 'Guardrail',       fixingDefault: 'through-bolt' },
        { value: 'demarcation',     label: 'Demarcation',     fixingDefault: 'through-bolt' },
      ]},
      walkways:      { label: 'Walkways', code: 'WK', subtypes: [
        { value: 'grating',   label: 'Grating',   fixingDefault: 'through-bolt' },
        { value: 'grip',      label: 'Grip',       fixingDefault: 'through-bolt' },
        { value: 'composite', label: 'Composite',  fixingDefault: 'through-bolt' },
        { value: 'aluminium', label: 'Aluminium',  fixingDefault: 'through-bolt' },
      ]},
      cradles:       { label: 'Cradles', code: 'CR', subtypes: [
        { value: 'bmu',       label: 'BMU',        fixingDefault: null },
        { value: 'gondola',   label: 'Gondola',    fixingDefault: null },
        { value: 'suspended', label: 'Suspended',   fixingDefault: null },
      ]},
    },
  },
};

const MATERIALS = [
  { value: 'stainless steel', label: 'Stainless Steel', code: 'SS' },
  { value: 'galvanised',      label: 'Galvanised',      code: 'GA' },
  { value: 'aluminium',       label: 'Aluminium',       code: 'AL' },
  { value: 'steel',           label: 'Steel',            code: 'ST' },
  { value: 'mild steel',      label: 'Mild Steel',       code: 'MS' },
  { value: 'concrete',        label: 'Concrete',         code: 'CN' },
  { value: 'wire rope',       label: 'Wire Rope',        code: 'WR' },
  { value: 'frp',             label: 'FRP / Composite',  code: 'FP' },
];

const FIXINGS = [
  { value: 'cast-in',      label: 'Cast-In',      code: 'CI' },
  { value: 'through-bolt', label: 'Through-Bolt',  code: 'TB' },
  { value: 'welded',       label: 'Welded',        code: 'WD' },
  { value: 'clamped',      label: 'Clamped',       code: 'CL' },
  { value: 'resin',        label: 'Resin / Chemical', code: 'RS' },
  { value: 'expansion',    label: 'Expansion',     code: 'EX' },
];

// ── Component ─────────────────────────────────────────────────────────

const AssetGenerationTool = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Mobile sidebar
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Step state
  const [step, setStep] = useState(1);

  // Step 1: Building
  const [buildings, setBuildings] = useState([]);
  const [buildingsLoading, setBuildingsLoading] = useState(true);
  const [buildingSearch, setBuildingSearch] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [propertyCode, setPropertyCode] = useState('');
  const [propertyCodeEdited, setPropertyCodeEdited] = useState(false);

  // Step 2: Sections
  const [selectedSection, setSelectedSection] = useState(null);

  // Step 3-5: Asset configurations
  const [assetConfigs, setAssetConfigs] = useState([]);
  // Each config: { assetTypeKey, subtypeValue, material, fixing, quantity }

  // Step 6: Preview
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [collisions, setCollisions] = useState([]);

  // Import state
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    const role = getUserRole();
    if (role !== 'cloud_staff' && role !== 'super_admin') {
      toast.error('Access denied');
      navigate('/login');
      return;
    }
    loadBuildings();
  }, [navigate]);

  const loadBuildings = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/buildings'), { headers });
      if (res.ok) setBuildings(await res.json());
    } catch { toast.error('Failed to load buildings'); }
    finally { setBuildingsLoading(false); }
  };

  // Auto-generate property code when building is selected
  const handleBuildingSelect = useCallback(async (building) => {
    setSelectedBuilding(building);
    setPropertyCodeEdited(false);
    setAssetConfigs([]);
    setPreviewRows([]);
    setStep(2);

    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/asset-gen/property-code/${building.id}`), { headers });
      if (res.ok) {
        const data = await res.json();
        setPropertyCode(data.propertyCode);
      }
    } catch { /* non-critical */ }
  }, []);

  const handleSectionSelect = (sectionKey) => {
    setSelectedSection(sectionKey);
    setAssetConfigs([]);
    setPreviewRows([]);
    setStep(3);
  };

  // Add a new asset config row
  const addAssetConfig = () => {
    const sectionData = SAFETY_SECTIONS[selectedSection];
    const firstType = Object.keys(sectionData.assetTypes)[0];
    const firstSubtype = sectionData.assetTypes[firstType].subtypes[0];

    setAssetConfigs(prev => [...prev, {
      id: Date.now(),
      assetTypeKey: firstType,
      subtypeValue: firstSubtype.value,
      material: 'stainless steel',
      fixing: firstSubtype.fixingDefault || 'through-bolt',
      quantity: 1,
    }]);
  };

  const updateConfig = (id, field, value) => {
    setAssetConfigs(prev => prev.map(c => {
      if (c.id !== id) return c;
      const updated = { ...c, [field]: value };

      // When asset type changes, reset subtype to first available
      if (field === 'assetTypeKey') {
        const sectionData = SAFETY_SECTIONS[selectedSection];
        const typeData = sectionData.assetTypes[value];
        if (typeData?.subtypes?.length) {
          updated.subtypeValue = typeData.subtypes[0].value;
          updated.fixing = typeData.subtypes[0].fixingDefault || 'through-bolt';
        }
      }

      // When subtype changes, update default fixing
      if (field === 'subtypeValue') {
        const sectionData = SAFETY_SECTIONS[selectedSection];
        const typeData = sectionData.assetTypes[c.assetTypeKey];
        const sub = typeData?.subtypes?.find(s => s.value === value);
        if (sub?.fixingDefault !== undefined) {
          updated.fixing = sub.fixingDefault || 'through-bolt';
        }
      }

      return updated;
    }));
  };

  const removeConfig = (id) => {
    setAssetConfigs(prev => prev.filter(c => c.id !== id));
  };

  // Preview generation
  const handlePreview = async () => {
    if (!assetConfigs.length) {
      toast.warning('Add at least one asset type');
      return;
    }

    setPreviewLoading(true);
    setPreviewRows([]);
    setCollisions([]);

    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/asset-gen/preview'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          propertyId: selectedBuilding.id,
          propertyCode: propertyCodeEdited ? propertyCode : undefined,
          assets: assetConfigs.map(c => ({
            assetTypeKey: c.assetTypeKey,
            subtype: c.subtypeValue,
            material: c.material,
            fixing: c.fixing,
            quantity: c.quantity,
          })),
        }),
      });

      if (res.ok) {
        const data = await res.json();
        setPreviewRows(data.rows);
        setCollisions(data.collisions || []);
        if (data.propertyCode) setPropertyCode(data.propertyCode);
        setStep(6);
      } else {
        const err = await res.json();
        toast.error(err.message || 'Failed to generate preview');
      }
    } catch {
      toast.error('Network error generating preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  // CSV Export
  const handleExportCsv = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/asset-gen/export-csv'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          propertyId: selectedBuilding.id,
          propertyCode: propertyCodeEdited ? propertyCode : undefined,
          assets: assetConfigs.map(c => ({
            assetTypeKey: c.assetTypeKey,
            subtype: c.subtypeValue,
            material: c.material,
            fixing: c.fixing,
            quantity: c.quantity,
          })),
        }),
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `cloudsafe-asset-import-${selectedBuilding.id}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success('CSV exported successfully');
      } else {
        toast.error('Failed to export CSV');
      }
    } catch {
      toast.error('Network error exporting CSV');
    }
  };

  // Direct Import
  const handleDirectImport = async () => {
    if (collisions.length > 0) {
      toast.error('Cannot import — resolve tag ID collisions first');
      return;
    }

    setImporting(true);
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/asset-gen/import'), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          propertyId: selectedBuilding.id,
          propertyCode: propertyCodeEdited ? propertyCode : undefined,
          assets: assetConfigs.map(c => ({
            assetTypeKey: c.assetTypeKey,
            subtype: c.subtypeValue,
            material: c.material,
            fixing: c.fixing,
            quantity: c.quantity,
          })),
        }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.imported > 0) {
          toast.success(`Successfully imported ${data.imported} assets into ${selectedBuilding.name}`);
        }
        if (data.failed > 0) {
          const errorDetail = data.errors?.length
            ? data.errors.map(e => `${e.tagId}: ${e.message}`).join('\n')
            : '';
          toast.warning(`${data.failed} assets failed to import`);
          if (errorDetail) console.error('Import errors:', errorDetail);
          if (data.errors?.length) {
            toast.error(data.errors[0].message, { autoClose: 8000 });
          }
        }
      } else {
        const err = await res.json();
        toast.error(err.message || 'Import failed');
      }
    } catch {
      toast.error('Network error during import');
    } finally {
      setImporting(false);
    }
  };

  // Filtered buildings
  const filteredBuildings = buildingSearch
    ? buildings.filter(b =>
        (b.name || '').toLowerCase().includes(buildingSearch.toLowerCase()) ||
        (b.address || '').toLowerCase().includes(buildingSearch.toLowerCase()) ||
        String(b.id).includes(buildingSearch)
      )
    : buildings;

  const sectionData = selectedSection ? SAFETY_SECTIONS[selectedSection] : null;
  const totalQuantity = assetConfigs.reduce((sum, c) => sum + (c.quantity || 0), 0);

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-inner">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}>
              <FaBars />
            </button>
            <h2 style={{ color: 'var(--text-header)', fontSize: '1rem', fontWeight: 700, margin: 0, fontFamily: 'var(--ui)' }}>
              <FaTag style={{ color: 'var(--primary)', marginRight: 8 }} />Asset Generation
            </h2>
          </div>
          {selectedBuilding && (
            <div className="agt-building-badge">
              <FaBuilding />
              <span>{selectedBuilding.name}</span>
              <code>{propertyCode}</code>
            </div>
          )}
        </div>
      </header>

      <div className="cs-layout">
      <CloudStaffSidebar active="asset-generation" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
      <main className="cs-content">
        {/* Page Header */}
        <div className="agt-page-header">
          <div>
            <h1><FaTag style={{ color: 'var(--primary)' }} /> Asset Generation Tool</h1>
            <p>Generate Hydrajaws-compatible asset tag IDs and bulk import into buildings</p>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="agt-steps-bar">
          {[
            { num: 1, label: 'Building', icon: FaBuilding },
            { num: 2, label: 'Section', icon: FaShieldAlt },
            { num: 3, label: 'Assets', icon: FaLayerGroup },
            { num: 6, label: 'Preview', icon: FaEye },
          ].map((s, i, arr) => (
            <React.Fragment key={s.num}>
              <button
                className={`agt-step-pill ${step >= s.num ? 'active' : ''} ${step === s.num ? 'current' : ''}`}
                onClick={() => { if (step >= s.num) setStep(s.num); }}
                disabled={step < s.num}
              >
                <s.icon />
                <span>{s.label}</span>
              </button>
              {i < arr.length - 1 && <FaChevronRight className="agt-step-arrow" />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step 1: Select Building ──────────────────────────── */}
        {step === 1 && (
          <div className="agt-panel">
            <h2 className="agt-panel-title"><FaBuilding /> Select Building</h2>

            <div className="agt-search-bar">
              <FaSearch />
              <input
                type="text" placeholder="Search by name, address, or ID..."
                value={buildingSearch} onChange={e => setBuildingSearch(e.target.value)}
              />
            </div>

            {buildingsLoading ? (
              <div className="agt-loading">Loading buildings...</div>
            ) : (
              <div className="agt-building-grid">
                {filteredBuildings.map(b => (
                  <button key={b.id} className="agt-building-card" onClick={() => handleBuildingSelect(b)}>
                    <div className="agt-building-card-top">
                      <FaBuilding />
                      <span className="agt-building-id">#{b.id}</span>
                    </div>
                    <h3>{b.name}</h3>
                    <p>{b.address || 'No address'}</p>
                  </button>
                ))}
                {filteredBuildings.length === 0 && (
                  <div className="agt-empty">No buildings match your search</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Select Section ──────────────────────────── */}
        {step === 2 && (
          <div className="agt-panel">
            <h2 className="agt-panel-title"><FaShieldAlt /> Select Safety Section</h2>

            {/* Property Code Override */}
            <div className="agt-property-code-row">
              <label>Property Code (used in tag IDs):</label>
              <input
                type="text" className="agt-code-input" value={propertyCode}
                onChange={e => { setPropertyCode(e.target.value.toUpperCase()); setPropertyCodeEdited(true); }}
                maxLength={10} spellCheck={false}
              />
              <span className="agt-code-hint">
                Format: <code>{propertyCode}-DSCISSTB-001</code>
              </span>
            </div>

            <div className="agt-section-grid">
              {Object.entries(SAFETY_SECTIONS).map(([key, section]) => (
                <button key={key} className="agt-section-card" onClick={() => handleSectionSelect(key)}>
                  <FaShieldAlt />
                  <h3>{section.label}</h3>
                  <p>{Object.keys(section.assetTypes).length} asset types</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 3-5: Configure Assets ──────────────────────── */}
        {step === 3 && sectionData && (
          <div className="agt-panel">
            <h2 className="agt-panel-title"><FaCogs /> Configure Assets</h2>
            <p className="agt-panel-sub">
              Add asset types, select subtypes and materials, and set quantities.
              Tag IDs follow the Hydrajaws format: <code>{propertyCode}-[TYPE][SUBTYPE][MATERIAL][FIXING]-[SEQ]</code>
            </p>

            {/* Config Rows */}
            <div className="agt-configs">
              {assetConfigs.map((config, idx) => {
                const typeData = sectionData.assetTypes[config.assetTypeKey];
                return (
                  <div key={config.id} className="agt-config-row">
                    <div className="agt-config-num">{idx + 1}</div>

                    {/* Asset Type */}
                    <div className="agt-config-field">
                      <label>Asset Type</label>
                      <select value={config.assetTypeKey} onChange={e => updateConfig(config.id, 'assetTypeKey', e.target.value)}>
                        {Object.entries(sectionData.assetTypes).map(([k, v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Subtype */}
                    <div className="agt-config-field">
                      <label>Subtype</label>
                      <select value={config.subtypeValue} onChange={e => updateConfig(config.id, 'subtypeValue', e.target.value)}>
                        {typeData?.subtypes?.map(s => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Material */}
                    <div className="agt-config-field">
                      <label>Material</label>
                      <select value={config.material} onChange={e => updateConfig(config.id, 'material', e.target.value)}>
                        {MATERIALS.map(m => (
                          <option key={m.value} value={m.value}>{m.label} ({m.code})</option>
                        ))}
                      </select>
                    </div>

                    {/* Fixing */}
                    <div className="agt-config-field agt-config-field-sm">
                      <label>Fixing</label>
                      <select value={config.fixing || ''} onChange={e => updateConfig(config.id, 'fixing', e.target.value)}>
                        <option value="">Auto</option>
                        {FIXINGS.map(f => (
                          <option key={f.value} value={f.value}>{f.label} ({f.code})</option>
                        ))}
                      </select>
                    </div>

                    {/* Quantity */}
                    <div className="agt-config-field agt-config-field-qty">
                      <label>Qty</label>
                      <div className="agt-qty-ctrl">
                        <button onClick={() => updateConfig(config.id, 'quantity', Math.max(1, config.quantity - 1))}>
                          <FaMinus />
                        </button>
                        <input
                          type="number" min="1" max="999" value={config.quantity}
                          onChange={e => updateConfig(config.id, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                        />
                        <button onClick={() => updateConfig(config.id, 'quantity', Math.min(999, config.quantity + 1))}>
                          <FaPlus />
                        </button>
                      </div>
                    </div>

                    {/* Remove */}
                    <button className="agt-config-remove" onClick={() => removeConfig(config.id)} title="Remove">
                      <FaTimes />
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="agt-config-actions">
              <button className="agt-add-btn" onClick={addAssetConfig}>
                <FaPlus /> Add Asset Type
              </button>

              {assetConfigs.length > 0 && (
                <div className="agt-config-summary">
                  <span>{assetConfigs.length} type{assetConfigs.length !== 1 ? 's' : ''}</span>
                  <span className="agt-dot">&middot;</span>
                  <span>{totalQuantity} total asset{totalQuantity !== 1 ? 's' : ''}</span>
                </div>
              )}

              <button
                className="agt-preview-btn"
                onClick={handlePreview}
                disabled={!assetConfigs.length || previewLoading}
              >
                {previewLoading ? 'Generating...' : <><FaEye /> Generate Preview</>}
              </button>
            </div>
          </div>
        )}

        {/* ── Step 6: Preview & Export ─────────────────────────── */}
        {step === 6 && previewRows.length > 0 && (
          <div className="agt-panel">
            <h2 className="agt-panel-title"><FaEye /> Preview — {previewRows.length} Assets</h2>

            {collisions.length > 0 && (
              <div className="agt-collision-alert">
                <FaExclamationTriangle />
                <span>{collisions.length} tag ID(s) already exist in this building: {collisions.join(', ')}</span>
              </div>
            )}

            {/* Tag format info */}
            <div className="agt-format-info">
              <FaInfoCircle />
              <span>
                Hydrajaws format: <code>{propertyCode}-[AssetType][Subtype][Material][Fixing]-[Seq]</code>
                &nbsp;&mdash;&nbsp;e.g. <code>{previewRows[0]?.tagId}</code>
              </span>
            </div>

            {/* Preview Table */}
            <div className="agt-table-wrap">
              <table className="agt-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Tag ID</th>
                    <th>Asset Name</th>
                    <th>Type</th>
                    <th>Subtype</th>
                    <th>Material</th>
                    <th>Fixing</th>
                    <th>Seq</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, i) => (
                    <tr key={i} className={collisions.includes(row.tagId) ? 'agt-row-collision' : ''}>
                      <td className="agt-td-num">{i + 1}</td>
                      <td className="agt-td-tag"><code>{row.tagId}</code></td>
                      <td>{row.displayName}</td>
                      <td><span className="agt-badge">{row.assetTypeCode}</span></td>
                      <td>{row.subtypeCode}</td>
                      <td>{row.materialCode}</td>
                      <td>{row.fixingCode}</td>
                      <td className="agt-td-num">{String(row.sequence).padStart(3, '0')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Export / Import Actions */}
            <div className="agt-export-bar">
              <button className="agt-back-btn" onClick={() => setStep(3)}>
                Back to Configure
              </button>

              <div className="agt-export-actions">
                <button className="agt-csv-btn" onClick={handleExportCsv}>
                  <FaDownload /> Export CSV
                </button>
                <button
                  className="agt-import-btn"
                  onClick={handleDirectImport}
                  disabled={importing || collisions.length > 0}
                >
                  {importing ? 'Importing...' : <><FaFileImport /> Import Directly</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      </div>
    </div>
  );
};

export default AssetGenerationTool;
